import { logger } from '../../../../../utils/common/logger'

const LR_CUSTOM_OBJECT_NOT_FOUND_CODE = 1057

export class LoginRadiusApiClient {
    constructor(
        private apiKey: string,
        private jwtApp: string,
        private accessToken: string,
        private appName: string,
        private objectName = ''
        ) {
    }

    /**
     * Get profile from access token
     * https://www.loginradius.com/docs/api/v2/customer-identity-api/authentication/auth-read-profiles-by-token/
     *
     * @return {Promise<any>} profile
     */
     public async getProfile(): Promise<any> {
        // tslint:disable-next-line: max-line-length
        const profileResponse = await fetch(`https://api.loginradius.com/identity/v2/auth/account?apiKey=${this.apiKey}`, {
            headers: {
                'Authorization': `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            method: 'GET'
        })

        const profile = await profileResponse.json()

        if (profile.ErrorCode) {
            throw new Error(profile.Description)
        }

        return profile
    }

    /**
     * Get JWT token from access token
     * https://www.loginradius.com/docs/api/v2/single-sign-on/federated-sso/jwt-login/jwt-token/
     *
     * @return {Promise<string>} JWT token
     */
    public async getJwtToken(): Promise<string> {
        // tslint:disable-next-line: max-line-length
        const jwtTokenResponse = await fetch(`https://${this.appName}.hub.loginradius.com/api/jwt/${this.jwtApp}/token/?apiKey=${this.apiKey}&access_token=${this.accessToken}`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            method: 'GET'
        })

        const response = await jwtTokenResponse.json()

        if (response.ErrorCode) {
            throw new Error(response.Description)
        }

        return response.signature
    }

    /**
     * Validate the access token
     * https://www.loginradius.com/docs/api/v2/customer-identity-api/authentication/auth-validate-access-token/
     *
     * @returns {Promise<boolean>} token valid flag
     */
     public async validateAccessToken(): Promise<boolean> {
        // tslint:disable-next-line: max-line-length
        const validateTokenCall = await fetch(`https://api.loginradius.com/identity/v2/auth/access_token/Validate?apiKey=${this.apiKey}`, {
            headers: {
                'Authorization': `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            method: 'GET'
        })

        const response = await validateTokenCall.json()

        return !!response.access_token
    }

    /**
     * Invalidate the access token
     * https://www.loginradius.com/docs/api/v2/customer-identity-api/authentication/auth-invalidate-access-token/
     *
     */
     public async invalidateAccessToken() {
        // tslint:disable-next-line: max-line-length
        await fetch(`https://api.loginradius.com/identity/v2/auth/access_token/inValidate?apiKey=${this.apiKey}`, {
            headers: {
                'Authorization': `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            method: 'GET'
        })
    }

    /**
     * Update profile by access token
     * https://www.loginradius.com/docs/api/v2/customer-identity-api/authentication/auth-update-profile-by-token/
     *
     */
    public async updateProfileByToken(profile: any): Promise<any> {
        // tslint:disable-next-line: max-line-length
        const updateProfileCall = await fetch(`https://api.loginradius.com/identity/v2/auth/account?apiKey=${this.apiKey}`, {
            body: JSON.stringify(profile),
            headers: {
                'Authorization': `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json'
            },
            method: 'PUT'
        })

        const response = await updateProfileCall.json()

        if (!response.IsPosted) {
            logger('error', 'Profile update error', response.Description)
            throw new Error(response.Description)
        }
        return response
    }

    /**
     * Partial update custom object by access token
     * https://www.loginradius.com/docs/api/v2/customer-identity-api/custom-object/
     * custom-object-update-by-objectrecordid-and-token/
     *
     */
    public async partialUpdateCustomObjectByToken(data: any): Promise<any> {
        const existingCustomObject = await this.getCustomObjectByToken()
        if (existingCustomObject === undefined) {
            // no custom object found, create one
            const createResponse = await fetch(
                // tslint:disable-next-line: max-line-length
                `https://api.loginradius.com/identity/v2/auth/customobject?apiKey=${this.apiKey}&objectname=${this.objectName}`,
                {
                    body: JSON.stringify(data),
                    headers: {
                        'Authorization': `Bearer ${this.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    method: 'POST',
                }
            )
            const createdCustomObject = await createResponse.json()
            if (createdCustomObject.ErrorCode) {
                throw new Error(createdCustomObject.Description)
            }
            return createdCustomObject
        }
        // partial update of existing custom object
        const response = await fetch(
            // tslint:disable-next-line: max-line-length
            `https://api.loginradius.com/identity/v2/auth/customobject/${existingCustomObject.Id}?apiKey=${this.apiKey}&objectname=${this.objectName}&updatetype=partialreplace`,
            {
                body: JSON.stringify(data),
                headers: {
                    'Authorization': `Bearer ${this.accessToken}`,
                    'Content-Type': 'application/json',
                },
                method: 'PUT',
            }
        )
        const customObject = await response.json()
        if (customObject.ErrorCode) {
            throw new Error(customObject.Description)
        }
        return customObject
    }

    /**
     * Get custom object by access token
     * https://www.loginradius.com/docs/api/v2/customer-identity-api/custom-object/custom-object-by-token/
     *
     */
    public async getCustomObjectByToken(): Promise<any> {
        // tslint:disable-next-line: max-line-length
        const response = await fetch(
            // tslint:disable-next-line: max-line-length
            `https://api.loginradius.com/identity/v2/auth/customobject?apiKey=${this.apiKey}&objectname=${this.objectName}`,
            {
                headers: {
                    'Authorization': `Bearer ${this.accessToken}`,
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                method: 'GET',
            }
        )

        const customObject = await response.json()

        if (customObject.ErrorCode === LR_CUSTOM_OBJECT_NOT_FOUND_CODE) {
            return undefined
        }
        if (customObject.ErrorCode) {
            throw new Error(customObject.Description)
        }
        const data = customObject.data
        /*
        data is in the form:
        {
            "Count": 1,
            "data": [
                {
                    "DateCreated": "2023-04-05T22:41:29.124Z",
                    "DateModified": "2023-04-05T22:41:29.124Z",
                    "IsActive": true,
                    "IsDeleted": false,
                    "CustomObject": {
                        "recommendations": {
                            "a8": {
                                "categories": [
                                    "Sports",
                                    "Entertainement"
                                ]
                            }
                        }
                    },
                    "Id": "642df919e6d2df6b91309a86",
                    "Uid": "37ffd69fea0347c1b169a54e41b09170"
                }
            ]
        }
        */
        const filteredData = data.filter((record: any) =>
            (record.IsDeleted || false) === false && (record.IsActive === undefined? true: record.IsActive) === true)

        // There should always be zero or one object, but just in case, we'll sort by date created
        filteredData.sort((a: any, b: any) => {
            if (a.DateCreated > b.DateCreated) {
                return 1
            }
            return -1
        })
        if (filteredData.length === 0) {
            return undefined
        }
        return data[data.length - 1]
    }
}
